import { formatEther } from 'viem'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { RoleGate } from '@/components/auth/RoleGate'
import type { ReferrerRow } from '@/hooks/chain/useReferralRoster'

const STATUS_BADGE: Record<
    ReferrerRow['status'],
    { label: string; variant: 'default' | 'warning' | 'destructive' }
> = {
    active: { label: 'Active', variant: 'default' },
    suspended: { label: 'Suspended', variant: 'warning' },
    terminated: { label: 'Terminated', variant: 'destructive' },
}

export type ReferrerActionKind = 'suspend' | 'reactivate' | 'terminate' | 'clawback'

export interface ReferrerRosterRowProps {
    row: ReferrerRow
    /** Disables the lifecycle buttons (mutation in flight or missing OPERATOR_ROLE). */
    actionsDisabled: boolean
    onAction: (kind: ReferrerActionKind) => void
}

// One registered referrer: code, status, effective share, balances, and the
// status-appropriate lifecycle actions (admin console role required to see them;
// the ReferralManager's OPERATOR_ROLE required on-chain to execute them).
export function ReferrerRosterRow({ row, actionsDisabled, onAction }: ReferrerRosterRowProps) {
    const badge = STATUS_BADGE[row.status]
    return (
        <div
            data-testid={`referrer-row-${row.address}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
        >
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{row.code}</span>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-muted-foreground text-xs">
                        {row.effectiveShareBps / 100}% of operator fee
                    </span>
                </div>
                <div className="text-muted-foreground flex items-center gap-3 text-xs">
                    <AddressDisplay address={row.address} />
                    <span>accrued {formatEther(row.accrued)} ETH</span>
                    <span>lifetime {formatEther(row.lifetimeEarned)} ETH</span>
                </div>
            </div>
            <RoleGate roles={['admin']}>
                <div className="flex gap-2">
                    {/* Lifecycle + clawback are all OPERATOR_ROLE-gated on-chain. */}
                    {row.status === 'active' && (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={actionsDisabled}
                            onClick={() => onAction('suspend')}
                        >
                            Suspend
                        </Button>
                    )}
                    {row.status === 'suspended' && (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={actionsDisabled}
                            onClick={() => onAction('reactivate')}
                        >
                            Reactivate
                        </Button>
                    )}
                    {row.status !== 'terminated' && (
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={actionsDisabled}
                            onClick={() => onAction('terminate')}
                        >
                            Terminate
                        </Button>
                    )}
                    {row.status === 'terminated' && (
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={actionsDisabled}
                            onClick={() => onAction('clawback')}
                        >
                            Clawback
                        </Button>
                    )}
                </div>
            </RoleGate>
        </div>
    )
}
